import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Separator } from '@/Components/ui/separator';
import { getColor } from '@/config/theme';
import { formatSalaryDisplay } from '../../../CompanyAdmin/utils/salaryUtils';

export const ReviewStep = ({
  formData,
  requiredSkillTags,
  optionalSkillTags,
  softSkillTags,
  onEditStep
}) => {
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatText = (text) => {
    if (!text) return 'Not specified';
    const stripped = stripHtml(text);
    return stripped || 'Not specified';
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Review Your Job Posting</h2>
        <p className="text-muted-foreground">
          Please review all the information below. You can edit any section by clicking the "Edit" button.
        </p>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Basic Information</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditStep(0)}
          >
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">Job Title</div>
              <div className="text-base font-semibold">{formData.jobTitle || 'Not specified'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Job Type</div>
              <div className="text-base">{formData.jobType || 'Not specified'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Location</div>
              <div className="text-base">{formData.companyLocation || 'Not specified'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Experience</div>
              <div className="text-base">
                {formData.minExperience 
                  ? `${formData.minExperience}${formData.maxExperience ? ` - ${formData.maxExperience}` : '+'} years`
                  : 'Not specified'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Employment Type</div>
              <div className="text-base">{formData.employmentType || 'Not specified'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Salary Range</div>
              <div className="text-base">{formatSalaryDisplay(formData.salaryRange)}</div>
            </div>
            {formData.applicationDeadline && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Application Deadline</div>
                <div className="text-base">{formData.applicationDeadline}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Job Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Job Details</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditStep(1)}
          >
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">Job Summary</div>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: formData.jobSummary || '<p class="text-muted-foreground">Not specified</p>' }}
            />
          </div>
          <Separator />
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">Key Responsibilities</div>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: formData.keyResponsibilities || '<p class="text-muted-foreground">Not specified</p>' }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Skills & Qualifications */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Skills & Qualifications</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditStep(2)}
          >
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {requiredSkillTags.length > 0 && (
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">Required Skills</div>
              <div className="flex flex-wrap gap-2">
                {requiredSkillTags.map((skill, index) => (
                  <Badge
                    key={index}
                    variant="default"
                    className="flex items-center gap-1"
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                    }}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {optionalSkillTags.length > 0 && (
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">Optional Skills</div>
              <div className="flex flex-wrap gap-2">
                {optionalSkillTags.map((skill, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {softSkillTags.length > 0 && (
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">Soft Skills</div>
              <div className="flex flex-wrap gap-2">
                {softSkillTags.map((skill, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {formData.criticalSkills && formData.criticalSkills.length > 0 && (
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">Critical Skills</div>
              <div className="flex flex-wrap gap-2">
                {formData.criticalSkills.map((skill, index) => (
                  <Badge
                    key={index}
                    variant="default"
                    className="flex items-center gap-1"
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                    }}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {formData.preferredQualification && (
            <>
              <Separator />
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Preferred Qualifications</div>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.preferredQualification || '<p class="text-muted-foreground">Not specified</p>' }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Company & Work Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Company & Work Details</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditStep(3)}
          >
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.workHours && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Work Hours</div>
                <div className="text-base">{formData.workHours}</div>
              </div>
            )}
            {formData.travelRequirement && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Travel Requirement</div>
                <div className="text-base">{formData.travelRequirement}</div>
              </div>
            )}
          </div>
          {formData.companyCulture && (
            <>
              <Separator />
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Company Culture</div>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.companyCulture || '<p class="text-muted-foreground">Not specified</p>' }}
                />
              </div>
            </>
          )}
          {formData.hiringProcess && (
            <>
              <Separator />
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Hiring Process</div>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.hiringProcess || '<p class="text-muted-foreground">Not specified</p>' }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Preferences</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditStep(4)}
          >
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {formData.preferrenceOne && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Preference 1</div>
                <div className="text-base">{formData.preferrenceOne}</div>
              </div>
            )}
            {formData.preferrenceTwo && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Preference 2</div>
                <div className="text-base">{formData.preferrenceTwo}</div>
              </div>
            )}
            {formData.preferrenceThree && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Preference 3</div>
                <div className="text-base">{formData.preferrenceThree}</div>
              </div>
            )}
          </div>
          <Separator />
          <div className="flex items-center space-x-2">
            <div className="text-sm font-medium text-muted-foreground">Mandatory Profile:</div>
            <div className="text-base">{formData.mandatoryProfile ? 'Yes' : 'No'}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
